import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Heart } from "lucide-react";
import { api } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { toast } from "sonner";

// Shared per-page cache so a listing of many cards makes at most one fetch each.
let favSet = null, favPromise = null;
let evSet = null, evPromise = null;
const getFavs = () => {
  if (!favPromise) favPromise = api.get("/me/favorites").then((r) => (favSet = new Set(r.data.map((b) => b.slug)))).catch(() => (favSet = new Set()));
  return favPromise;
};
const getEvents = () => {
  if (!evPromise) evPromise = api.get("/me/events").then((r) => (evSet = new Set(r.data.map((e) => e.slug)))).catch(() => (evSet = new Set()));
  return evPromise;
};
export const resetCardHeartCache = () => { favSet = favPromise = evSet = evPromise = null; };

// kind: "venue" (favorite) | "event" (interested). subjectKey = slug.
export default function CardHeart({ kind, subjectKey, label }) {
  const { user } = useAuth();
  const nav = useNavigate();
  const isCustomer = user && user.role === "customer";
  const [active, setActive] = useState(false);

  useEffect(() => {
    if (!isCustomer) return;
    (kind === "venue" ? getFavs() : getEvents()).then((set) => setActive(set.has(subjectKey)));
  }, [isCustomer, kind, subjectKey]);

  const toggle = async (ev) => {
    ev.preventDefault(); ev.stopPropagation();
    if (!user) { toast("Sign in to save", { description: "Create a free account to save and follow." }); nav("/account/login"); return; }
    if (!isCustomer) return;
    try {
      if (kind === "venue") {
        const { data } = await api.post(`/me/favorites/${subjectKey}`);
        setActive(data.favorited);
        (data.favorited ? favSet?.add(subjectKey) : favSet?.delete(subjectKey));
        toast.success(data.favorited ? `Saved ${label || "venue"}` : "Removed");
      } else {
        const newActive = !active;
        await api.post(`/me/events/${subjectKey}`, { status: newActive ? "interested" : "none" });
        setActive(newActive);
        (newActive ? evSet?.add(subjectKey) : evSet?.delete(subjectKey));
        toast.success(newActive ? "Added to your events" : "Removed");
      }
    } catch { toast.error("Something went wrong"); }
  };

  return (
    <button onClick={toggle} data-testid={`card-heart-${kind}-${subjectKey}`} aria-label="Save"
      className={`w-8 h-8 flex items-center justify-center backdrop-blur-md transition-colors ${active ? "bg-[#FF0055] text-white" : "bg-black/50 text-white hover:bg-[#FF0055]"}`}>
      <Heart size={15} fill={active ? "#fff" : "none"} />
    </button>
  );
}
